import { Loader2 } from "lucide-react";
import { Modal } from "../../components/Modal";
import { StatusBadge } from "../../components/StatusBadge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import type { LogReviewJustificationHistory } from "../../types/logReview";
import { logReviewStatusVariant } from "./logReviewChartUtils";

/**
 * Minimal shape needed by the popup — satisfied by both the main
 * `LogReviewRow` and the Insights `LogInsightRow` call sites.
 */
export interface LogReviewInfoRow {
  changeRequestId: string;
  justificationHistory: LogReviewJustificationHistory[];
}

export interface LogReviewInfoModalProps {
  open: boolean;
  row: LogReviewInfoRow | null;
  onOpenChange: (open: boolean) => void;
  isLoading?: boolean;
}

/**
 * Per-CR "Info" popup: the justification → review → approval audit trail.
 *
 * Built on the raw table primitives rather than the shared DataTable because
 * the design groups columns under User / Log Reviewer / Log Approver spans —
 * a two-tier header the shared component has no notion of.
 */
export const LogReviewInfoModal = ({
  open,
  row,
  onOpenChange,
  isLoading = false,
}: LogReviewInfoModalProps) => {
  const history = row?.justificationHistory ?? [];

  const headClass = "text-center whitespace-nowrap";
  const groupClass = `${headClass} border-l border-border font-semibold`;

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title={row ? `Details : ${row.changeRequestId}` : "Details"}
      variant="extraWide"
    >
      {isLoading ? (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          Loading…
        </div>
      ) : history.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted-foreground">
          No Data Available
        </p>
      ) : (
        <div className="overflow-x-auto">
          <Table className="min-w-[64rem]">
            <TableHeader>
              <TableRow>
                <TableHead colSpan={4} className={groupClass}>
                  User
                </TableHead>
                <TableHead colSpan={3} className={groupClass}>
                  Log Reviewer
                </TableHead>
                <TableHead colSpan={3} className={groupClass}>
                  Log Approver
                </TableHead>
                <TableHead rowSpan={2} className={groupClass}>
                  Status
                </TableHead>
              </TableRow>
              <TableRow>
                <TableHead className={`${headClass} border-l border-border`}>
                  Justification
                </TableHead>
                <TableHead className={headClass}>Justified By</TableHead>
                <TableHead className={headClass}>Justified On</TableHead>
                <TableHead className={headClass}>Attachments</TableHead>
                <TableHead className={`${headClass} border-l border-border`}>
                  Comment
                </TableHead>
                <TableHead className={headClass}>Commented By</TableHead>
                <TableHead className={headClass}>Commented On</TableHead>
                <TableHead className={`${headClass} border-l border-border`}>
                  Comment
                </TableHead>
                <TableHead className={headClass}>Commented By</TableHead>
                <TableHead className={headClass}>Commented On</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map((h, i) => (
                <TableRow key={`${h.justifiedBy}-${h.justifiedOn}-${i}`}>
                  <TableCell className="border-l border-border">
                    {h.justification}
                  </TableCell>
                  <TableCell>{h.justifiedBy}</TableCell>
                  <TableCell className="text-center whitespace-nowrap">
                    {h.justifiedOn}
                  </TableCell>
                  <TableCell>{h.attachments}</TableCell>
                  <TableCell className="border-l border-border">
                    {h.reviewerComment}
                  </TableCell>
                  <TableCell>{h.reviewerCommentedBy}</TableCell>
                  <TableCell className="text-center whitespace-nowrap">
                    {h.reviewerCommentedOn}
                  </TableCell>
                  <TableCell className="border-l border-border">
                    {h.approverComment}
                  </TableCell>
                  <TableCell>{h.approverCommentedBy}</TableCell>
                  <TableCell className="text-center whitespace-nowrap">
                    {h.approverCommentedOn}
                  </TableCell>
                  <TableCell className="border-l border-border">
                    <StatusBadge
                      status={logReviewStatusVariant(h.status)}
                      label={h.status}
                      dot={false}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Modal>
  );
};
